import { Injectable } from '@angular/core';
import { PaymentBatchService } from '../../../paymentBatch.service';
import { PaymentBatchPaymentsBulkUpdateModalResult } from './paymentBatchPaymentsBulkUpdateModal.component';

export interface PaymentBatchPaymentsBulkUpdateExecutionParams {
    paymentBatchId: number;
    selection: { selectAllRows: boolean; selectedRows: number[] };
    paymentIds: number[];
    update: PaymentBatchPaymentsBulkUpdateModalResult;
}

export interface PaymentBatchPaymentsBulkUpdateOperationOutcome<T = void> {
    requested: boolean;
    status: 'not-requested' | 'succeeded' | 'partially-succeeded' | 'failed';
    result?: T;
    error?: unknown;
}

export interface PaymentBatchPaymentsBulkUpdateOutcome {
    rereview: PaymentBatchPaymentsBulkUpdateOperationOutcome;
    approver: PaymentBatchPaymentsBulkUpdateOperationOutcome<Core.PaymentBatchBulkUpdateApproverResultModel>;
    changed: boolean;
}

@Injectable()
export class PaymentBatchPaymentsBulkUpdateService {
    constructor(private readonly paymentBatchService: PaymentBatchService) {}

    async execute(params: PaymentBatchPaymentsBulkUpdateExecutionParams): Promise<PaymentBatchPaymentsBulkUpdateOutcome> {
        const outcome: PaymentBatchPaymentsBulkUpdateOutcome = {
            rereview: { requested: params.update.markRereviewed, status: 'not-requested' },
            approver: { requested: !!params.update.approverContactId, status: 'not-requested' },
            changed: false,
        };

        if (outcome.rereview.requested) {
            try {
                await this.paymentBatchService.paymentBatchDetailsPaymentBulkReReview({
                    paymentBatchId: params.paymentBatchId,
                    selectAllRows: params.selection.selectAllRows,
                    selectedRows: params.selection.selectedRows,
                });
                outcome.rereview.status = 'succeeded';
                outcome.changed = true;
            } catch (error) {
                outcome.rereview.error = error;
                outcome.rereview.status = 'failed';
            }
        }

        if (outcome.approver.requested) {
            try {
                outcome.approver.result = await this.paymentBatchService.bulkUpdatePaymentApprover({
                    paymentBatchId: params.paymentBatchId,
                    selectAllRows: false,
                    selectedRows: params.paymentIds,
                    approverContactId: params.update.approverContactId,
                });
                const successCount = outcome.approver.result?.successCount ?? 0;
                const failureCount = outcome.approver.result?.failures?.length ?? 0;
                outcome.approver.status =
                    failureCount === 0
                        ? 'succeeded'
                        : successCount > 0
                          ? 'partially-succeeded'
                          : 'failed';
                outcome.changed = outcome.changed || successCount > 0;
            } catch (error) {
                outcome.approver.error = error;
                outcome.approver.status = 'failed';
            }
        }

        return outcome;
    }
}
